import {
    COGNITO_AUTHENTICATOR,
    COGNITO_IDENTITY_POOL_ID,
    COGNITO_IDENTITY_POOL_LOGIN,
    COGNITO_IDENTITY_POOL_REGION,
} from "src/config";
import AWS from "aws-sdk";
import { logger } from "src/utils/logger";

let credentialPromise = null;

export async function getAwsCredentials() {
    if (credentialPromise) {
        return credentialPromise;
    }

    if (AWS.config.credentials?.accessKeyId && !AWS.config.credentials.needsRefresh()) {
        return Promise.resolve(AWS.config.credentials);
    }

    credentialPromise = (async () => {
        try {
            const authSession = await getCognitoAuthSession();
            const logins = {};
            logins[COGNITO_IDENTITY_POOL_LOGIN] = authSession.getIdToken().getJwtToken();
            AWS.config.credentials = new AWS.CognitoIdentityCredentials(
                {
                    IdentityPoolId: COGNITO_IDENTITY_POOL_ID,
                    Logins: logins,
                },
                { region: COGNITO_IDENTITY_POOL_REGION }
            );
            await AWS.config.credentials.getPromise();
            return AWS.config.credentials;
        } catch (error) {
            logger.error('Failed to get Cognito credentials', error);
            throw new Error('Authentication failed. Please try logging in again or refreshing the page.');
        } finally {
            credentialPromise = null;
        }
    })();

    return credentialPromise;
}

let authSessionPromise = null;

export const getCognitoAuthSession = () => {
  const auth = COGNITO_AUTHENTICATOR;
  auth.useCodeGrantFlow();

  const session = auth.getSignInUserSession();
  if (session.isValid()) {
    return Promise.resolve(session);
  }

  if (authSessionPromise) {
    return authSessionPromise;
  }

  const attempt = startCognitoAuthSession(auth).finally(() => {
    if (authSessionPromise === attempt) {
      authSessionPromise = null;
    }
  });
  authSessionPromise = attempt;

  return attempt;
};

const startCognitoAuthSession = (auth) => {
  return new Promise((resolve, reject) => {
    let retried = false
    auth.userhandler = {
      onFailure: (err) => {
        logger.error("Failed to authenticate");
        localStorage.clear();
        if(!retried) {
          logger.info("Going to clear local storage to get the latest refesh token");
          auth.getSession();
          retried = true;
        }
        removeQueryFromLocation();
        reject(err);
      },
      onSuccess: (result) => {
        logger.info("Authenticated successfully");
        removeQueryFromLocation();
        resolve(auth.getSignInUserSession());
      },
    };
    const href = window.location.href;
    const session = auth.getSignInUserSession();
    if (session.isValid()) {
      resolve(session);
    } else if (href.indexOf("?code=") > 0) {
      // This is required because Cognito needs to get the authentication result from the query string
      // The parsing is done asynchronously, and the result will be passed to the userHandler.
      // Once the result is parsed, onSuccess userhandler will resolve the promise.
      auth.parseCognitoWebResponse(href);
    } else {
      // Cognito SDK will handle session refresh / authentication.
      auth.getSession();
    }
  });
};

export async function getJwtToken() {
	try {
		const session = await getCognitoAuthSession();
		if (!session) {
			throw new Error("No valid session found");
		}
		const accessToken = session.getAccessToken();
		if (!accessToken) {
			throw new Error("No access token found in session");
		}
		const jwtToken = accessToken.getJwtToken();
		if (!jwtToken) {
			throw new Error("No JWT token found in access token");
		}
		return jwtToken;
	} catch (error) {
		logger.error("Failed to get JWT token:", error);
		throw error;
	}
}

function removeQueryFromLocation() {
  // Replace the href because the Cognito passes the OAuth2 grant code in the query string
  // And the grant code is not reusable
  if (window.history.length > 0) {
    const newHref = window.location.href.split('?code')[0];
    window.history.replaceState(undefined, 'SNOW App', newHref);
  }

}
